'use client';

import {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type FC,
} from 'react';
import {
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Heading,
  Input,
  Loading,
  Radio,
  Text,
  Separator,
} from '@/_components';
import { Documents } from './components/Documents';
import { Options } from './components/Options';
import { getQuestionnaire } from '@/_api/client-questionnaires';
import { getNotifications } from '@/_api/admin-notifications';
import {
  exportQuestionnaireToSpreadsheet,
  exportQuestionnaires,
  type ExportQuestionnairesAttrs,
} from '@/_api/admin-questionnaires';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { saveAs } from '@/_utils/files';
import { Questionnaire } from '@/_types/models/questionnaire';
import { isEmpty } from '@/_utils/strings';
import {
  unknownDocumentDescription,
  getDocumentOrder,
} from '@/_utils/documents';
import type {
  ExportQuestionnairesDialogProps,
  ExportFormatOptions,
} from './ExportQuestionnairesDialog.types';
import { ExportQuestionnaireConfirmationDialog } from './components/ExportQuestionnaireConfirmationDialog';
import { Document } from '@/_types/documents';
import { Company } from '@/_types/models/company';
import { getCompanyById } from '@/_api/admin-companies';
import { getAdminEntitiesByIds } from '@/_api/v2/admin-entities';

export const ExportQuestionnairesDialog: FC<
  ExportQuestionnairesDialogProps
> = ({ isOpen, onClose, onClosePreview, questionnaires }) => {
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] =
    useState<boolean>(false);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | undefined>(
    undefined,
  );
  const [company, setCompany] = useState<Company | undefined>(undefined);
  const [format, setFormat] = useState<ExportFormatOptions>('pdf');

  const currentDate: string = new Date()
    .toLocaleDateString('en-US')
    .replace(/\//g, '-');

  const buildSaveAsName = (
    questionnaire?: Questionnaire,
    selectedCompany?: Company,
  ): string => {
    if (!questionnaire || (!selectedCompany && !company)) {
      return '';
    }

    const companyName = selectedCompany?.name || company?.name;
    return `${companyName} - ${questionnaire.name} (${questionnaire?.tax_year}) ${currentDate}`;
  };

  const [saveAsName, setSaveAsName] = useState(buildSaveAsName(questionnaire)); // for single questionnaire
  const [saveAsNames, setSaveAsNames] = useState<{ [key: number]: string }>({}); // for multiple questionnaires
  const [isLoading, setIsLoading] = useState(false);
  const [includeTOC, setIncludeTOC] = useState(false);
  const [includePageNumbers, setIncludePageNumbers] = useState(false);
  const [includeDocuments, setIncludeDocuments] = useState(true);
  const [includeAnnotations, setIncludeAnnotations] = useState(false);
  const [selectedDMS, setSelectedDMS] = useState<string[]>([]);
  const [removeAllNosAndNAs, setRemoveAllNosAndNAs] = useState(false);
  const [convertAllImagesToPDF, setConvertAllImagesToPDF] = useState(false);

  const [documents, setDocuments] = useState<Document[]>([]);

  const selectedQuestionnaireCount: number = questionnaires?.length;
  const isSingleQuestionnaire = Boolean(
    questionnaires && questionnaires.length === 1 && questionnaires[0],
  );

  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);

  const firstQuestionnaireId: number | undefined = questionnaires[0]?.id;
  const companyId: number | undefined = questionnaires[0]?.company_id;

  const fetchQuestionnaire = useCallback(async () => {
    if (!token || typeof companyId !== 'number') return;

    try {
      setIsLoading(true);

      const response = await getQuestionnaire({
        companyId,
        questionnaireId: firstQuestionnaireId,
        token,
      });

      const company = await getCompanyById({
        token,
        companyId: companyId!,
      });

      if (!company?.error && !response?.error) {
        setCompany(company);
        setQuestionnaire(response);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, firstQuestionnaireId, token]);

  const fetchSaveAsNames = async () => {
    if (!token || !questionnaires) {
      return {};
    }

    const saveAsNames: { [key: number]: string } = {};

    const companyIds = questionnaires.map((q: Questionnaire) => q.company_id);
    const companies = await getAdminEntitiesByIds({
      token,
      ids: companyIds,
    });

    if (!Array.isArray(companies)) {
      return {};
    }

    await Promise.all(
      questionnaires.map(async (q: Questionnaire) => {
        const selectedCompany = companies.find(
          (c: Company) => c.id === q.company_id,
        );

        if (selectedCompany) {
          saveAsNames[q.id] = buildSaveAsName(q, selectedCompany);
        }
      }),
    );

    return saveAsNames;
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isSingleQuestionnaire) {
      fetchQuestionnaire();
    } else {
      fetchSaveAsNames().then((names) => setSaveAsNames(names));
    }
  }, [isOpen, isSingleQuestionnaire, fetchQuestionnaire]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (questionnaire) {
      setSaveAsName(buildSaveAsName(questionnaire));
    }
  }, [questionnaire]); // eslint-disable-line react-hooks/exhaustive-deps

  // when unmounted, reset all state:
  useEffect(() => {
    return () => {
      setFormat('pdf');
      setSaveAsName('');
      setSaveAsNames({});
      setIncludeTOC(false);
      setIncludePageNumbers(false);
      setIncludeDocuments(true);
      setIncludeAnnotations(false);
      setRemoveAllNosAndNAs(false);
      setConvertAllImagesToPDF(false);
      setDocuments([]);
      setIsLoading(false);
    };
  }, []);

  const title: string =
    selectedQuestionnaireCount > 1
      ? `Export Questionnaires (${selectedQuestionnaireCount})`
      : `Export Questionnaire ${questionnaire?.name ? `(${questionnaire?.name})` : ''}`;

  const formatExtForInput = (): string => {
    switch (format) {
      case 'pdf':
        return '.pdf';
      case 'spreadsheet':
        return '.xls';
      case 'zipped':
        return '.zip';
      default:
        return '';
    }
  };

  const exportedFormatOptions: Array<{
    name: string;
    value: ExportFormatOptions;
  }> = useMemo(() => {
    const options: Array<{
      name: string;
      value: ExportFormatOptions;
    }> = [
      { name: 'PDF', value: 'pdf' },
      { name: 'Spreadsheet', value: 'spreadsheet' },
      { name: 'Zipped folder', value: 'zipped' },
    ];

    if (preferences?.scan_and_auto_flow_export_enabled) {
      options.push({ name: 'Scan and AutoFlow', value: 'scan_and_auto_flow' });
    }

    return options;
  }, [preferences]);

  const onIncludeDocumentsChange = (isChecked: boolean) => {
    setIncludeDocuments(isChecked);
  };

  // NOTE @chris needs to be a memo
  useEffect(() => {
    if (
      questionnaire &&
      (includeDocuments ||
        // always show documents for Scan and AutoFlow
        format === 'scan_and_auto_flow')
    ) {
      setDocuments(
        questionnaire?.extracted_documents
          ?.filter((document: any) => {
            return (
              !isEmpty(document.source) &&
              document.source !== 'Questionnaire' &&
              document.source !== 'Imported' &&
              // Do not include bulk documents that have been split
              document.status !== 'split_succeeded' &&
              // Do not include child documents that have not been reviewed by firm users
              (document.parent_document_id === null ||
                !['review_required', 'action_required'].includes(
                  document.status,
                ))
            );
          })
          .map((document: any) => {
            return {
              id: document.id,
              source: document.source,
              name: document.name,
              description: !isEmpty(document.description)
                ? document.description
                : unknownDocumentDescription(),
              included: true,
              needsDescription: !!isEmpty(document.description),
            };
          })
          .sort((a: any, b: any) => {
            return (
              getDocumentOrder(a, preferences) -
              getDocumentOrder(b, preferences)
            );
          }) || [],
      );
    } else {
      setDocuments([]);
    }
  }, [includeDocuments, questionnaire, format, preferences]);

  const onToggleDocument = (id: number) => {
    setDocuments((prevDocuments) => {
      return prevDocuments.map((document) => {
        if (document.id === id) {
          return {
            ...document,
            included: !document.included,
          };
        }
        return document;
      });
    });
  };

  const onDescriptionUpdated = (id: number, description: string) => {
    setDocuments((prevDocuments) => {
      return prevDocuments.map((document) => {
        if (document.id === id) {
          return {
            ...document,
            description,
          };
        }
        return document;
      });
    });
  };

  const onDocumentDragEnd = (sourceIndex: number, destinationIndex: number) => {
    const reorderedDocuments = [...documents];
    const [removed] = reorderedDocuments.splice(sourceIndex, 1);

    reorderedDocuments.splice(destinationIndex, 0, removed);
    setDocuments(reorderedDocuments);
  };

  const onFormSubmit = async (event: FormDataEvent) => {
    event.preventDefault();

    setIsLoading(true);

    if (format === 'spreadsheet') {
      try {
        questionnaires.forEach(async (questionnaire: Questionnaire) => {
          const response = (await exportQuestionnaireToSpreadsheet({
            id: questionnaire?.id,
            companyId: questionnaire?.company_id,
            token,
          })) as any;

          const fileText = await response.text();
          const newSaveAsName = saveAsName || buildSaveAsName(questionnaire);
          saveAs(fileText, `${newSaveAsName}.xls`);
        });

        addToast('success', 'Exporting questionnaire to spreadsheet');
        setTimeout(() => {
          getNotifications({ token });
        }, 2000);
        onClose();
      } catch (error) {
        addToast('error', 'Failed to export questionnaire to spreadsheet', {
          error,
        });
      } finally {
        setIsLoading(false);
      }

      return;
    }

    const questionnaireIds = questionnaires.map((q: Questionnaire) => q.id);

    const params: ExportQuestionnairesAttrs = {
      questionnaire_ids: questionnaireIds,
      include_annotations:
        (format === 'pdf' || format === 'zipped') && includeAnnotations,
      include_documents: includeDocuments,
      include_toc: includeTOC,
      include_pages: includePageNumbers,
      remove_nos_and_na_answers: format === 'pdf' && removeAllNosAndNAs,
      convert_images_to_pdfs: format === 'zipped' && convertAllImagesToPDF,
      save_as_name: isSingleQuestionnaire ? saveAsName : null,
      save_as_names: isSingleQuestionnaire ? null : saveAsNames,
      documents_to_include:
        questionnaireIds.length === 1
          ? includeDocuments
            ? documents
                .filter((document: any) => {
                  return document.included;
                })
                .map((d: any) => {
                  if (!d?.path && d.source) {
                    d.path = d.source;
                  }

                  return {
                    id: d.id,
                    name: d.name,
                    path: d.path,
                  };
                })
            : []
          : null,
      documents_to_exclude: includeDocuments
        ? documents
            .filter((document: any) => {
              return !document.included;
            })
            .map((d: any) => {
              if (!d?.path && d.source) {
                d.path = d.source;
              }

              return {
                id: d.id,
                name: d.name,
                path: d.path,
              };
            })
        : [],
      trigger_webhook: selectedDMS.length > 0,
      export_to_platforms: selectedDMS,
      zipped: format === 'zipped',
      format: format === 'scan_and_auto_flow' ? 'Scan and AutoFlow' : undefined,
    };

    try {
      await exportQuestionnaires({ ...params, token });

      selectedQuestionnaireCount === 1 && setConfirmationDialogOpen(true);
      setTimeout(() => {
        getNotifications({ token });
      }, 2000);
    } catch (error) {
      addToast('error', 'Failed to export questionnaire', { error });
    } finally {
      setIsLoading(false);
      onClose();
      onClosePreview();
    }
  };

  const onToggleConfirmationDialog = () => {
    setConfirmationDialogOpen(!isConfirmationDialogOpen);
  };

  const toggleSelectedDMS = useCallback((dms: string, checked: boolean) => {
    setSelectedDMS((prevSelectedDMS) => {
      return checked
        ? [...prevSelectedDMS, dms]
        : prevSelectedDMS.filter((selectedDMS) => selectedDMS !== dms);
    });
  }, []);

  return (
    <>
      <ExportQuestionnaireConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onClose={onToggleConfirmationDialog}
      />
      <Dialog isOpen={isOpen} onClose={onClose} title={title}>
        <Dialog.Content>
          <Flex isColumn={true} gap={8}>
            {isLoading && <Loading size={20} />}

            {/* Export Format */}
            <Flex isColumn={true} gap={4}>
              <Heading level={5}>Export format</Heading>
              {exportedFormatOptions.map((option) => {
                const onToggleOption = () => setFormat(option.value);

                return (
                  <Radio
                    key={option.value}
                    name="export-questionnaire-dialog"
                    label={option.name}
                    onChange={onToggleOption}
                    selectedValue={format}
                    value={option.value}
                  />
                );
              })}
            </Flex>

            {/* Format Options */}
            {format !== 'spreadsheet' && format !== 'scan_and_auto_flow' && (
              <Options
                convertAllImagesToPDF={convertAllImagesToPDF}
                selectedDMS={selectedDMS}
                selectableDMS={
                  !!preferences?.export_to_third_party_dms_enabled
                    ? (preferences?.export_to_platforms ?? [])
                    : []
                }
                format={format}
                includeTOC={includeTOC}
                includePageNumbers={includePageNumbers}
                includeDocuments={includeDocuments}
                includeAnnotations={includeAnnotations}
                annotationEnabled={!!preferences?.annotation_enabled}
                onIncludeDocumentsChange={onIncludeDocumentsChange}
                removeAllNosAndNAs={removeAllNosAndNAs}
                setConvertAllImagesToPDF={setConvertAllImagesToPDF}
                setIncludePageNumbers={setIncludePageNumbers}
                setIncludeTOC={setIncludeTOC}
                setIncludeAnnotations={setIncludeAnnotations}
                setRemoveAllNosAndNAs={setRemoveAllNosAndNAs}
                toggleSelectedDMS={toggleSelectedDMS}
              />
            )}

            {/* Documents */}
            {(includeDocuments || format === 'scan_and_auto_flow') &&
              isSingleQuestionnaire &&
              questionnaire && (
                <Documents
                  documents={documents}
                  companyId={questionnaire?.company_id!}
                  onToggleDocument={onToggleDocument}
                  onDescriptionUpdated={onDescriptionUpdated}
                  onDragEnd={onDocumentDragEnd}
                />
              )}

            {/* Save as */}
            {isSingleQuestionnaire && (
              <Flex isColumn={true} gap={4}>
                <Heading level={5}>Save as</Heading>
                <Flex gap={4} isYCentered={true}>
                  <Input
                    isLabelHidden
                    label="Save as"
                    type="text"
                    value={saveAsName}
                    onChange={(event: any) => setSaveAsName(event.target.value)}
                  />
                  <Text type="small">{formatExtForInput()}</Text>
                </Flex>
              </Flex>
            )}

            {/* Actions */}
            <Separator isLight={true} />
            <ButtonGroup align="right">
              <Button
                onClick={onClose}
                intent="secondary"
                isDisabled={isLoading}
                type="button"
                value="Cancel"
              />
              <Button
                onClick={onFormSubmit}
                intent="primary"
                isDisabled={isLoading}
                type="button"
                value="Export"
              />
            </ButtonGroup>
          </Flex>
        </Dialog.Content>
      </Dialog>
    </>
  );
};
